import { useApps } from '@auth/useApps'
import RecognizeButton from '@components/button/RecognizeButton'
import { colors } from '@globals/themes/defaultTheme'
import { onHover, spacing } from '@globals/themes/themeConstants'
import { useCurrentMember } from '@globals/useCurrentMember'
import { TenantMember } from '@kudos/http-client'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded'
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActionArea, { cardActionAreaClasses } from '@mui/material/CardActionArea'
import Divider from '@mui/material/Divider'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import UserAvatar from '@shared/avatar/UserAvatar'
import CelebrationBanners from '@shared/celebration/CelebrationBanners'
import { AppPermission } from '@shared/permissions'
import { getUserDisplayName } from '@shared/userDisplay/UserNameDisplay'
import { toRecognitionRecipient } from '@shared/utils/toRecognitionRecipient'
import { useT } from '@transifex/react'
import { getMemberProfilePreviewRegionId } from './memberProfilePreviewUtils'

const CARD_WIDTH = 320

const detailIconStyle: SxProps = {
  color: colors.blueMedium,
}

const cardStyle: SxProps = {
  width: CARD_WIDTH,
  borderRadius: spacing(2),
  boxShadow: 3,
}

// The rule runs edge to edge, so it has to escape the action area's padding.
const dividerStyle: SxProps = {
  mx: '-16px !important',
  p: 0,
}

// MUI shades the action area with an overlay element rather than a background
// colour, so the hover treatment has to be turned off there. The focus-visible
// highlight is left alone: keyboard users need to see where they are.
const cardActionAreaStyle: SxProps = {
  p: 2,
  [`${onHover} .${cardActionAreaClasses.focusHighlight}`]: {
    opacity: 0,
  },
}

export const MemberProfilePreviewCardSkeleton: React.FC = () => {
  const t = useT()

  return (
    <Card sx={{ ...cardStyle, p: 2 }} aria-busy aria-label={t('Profile preview')}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="circular" width={56} height={56} />
          <Stack spacing={0.5} flex={1}>
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="85%" />
          </Stack>
        </Stack>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="50%" />
      </Stack>
    </Card>
  )
}

type MemberProfilePreviewCardProps = {
  member: TenantMember
  onClose: () => void
  onNavigate: () => void
  previewRegionId?: string
}

const MemberProfilePreviewCard: React.FC<MemberProfilePreviewCardProps> = ({
  member,
  onClose,
  onNavigate,
  previewRegionId,
}) => {
  const t = useT()
  const { member: currentMember } = useCurrentMember()
  const [hasPermission] = useApps()
  const canRecognize =
    hasPermission('recognitionApp', AppPermission.RecognitionAppBrowse) ||
    hasPermission('recognitionApp', AppPermission.RecognitionAppUpgradeLicense)
  const isSelf = member.id === currentMember?.id

  const { avatarUrls, firstName, email, publicStaffProfile, events } = member
  const fullName = getUserDisplayName(member, t)
  const jobTitle = publicStaffProfile?.jobTitle
  const locationName = publicStaffProfile?.locations?.[0]?.name
  const showRecognize = canRecognize && !isSelf
  const hasDetails = !!jobTitle || !!locationName

  return (
    <Card
      id={previewRegionId ?? getMemberProfilePreviewRegionId(member.id)}
      role="region"
      sx={cardStyle}
      aria-label={t('Profile preview for {name}', { name: fullName })}>
      <CardActionArea
        onClick={onNavigate}
        sx={{ ...cardActionAreaStyle, pb: showRecognize ? 0 : 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <UserAvatar
              tenantMemberId={member.id}
              media={avatarUrls}
              size={56}
              hideAdornment
              enableProfilePreview={false}
            />
            <Stack spacing={0.25} minWidth={0}>
              <Typography variant="body2" noWrap translate="no">
                {fullName}
              </Typography>
              {email && (
                <Typography variant="caption" noWrap translate="no">
                  {email}
                </Typography>
              )}
            </Stack>
          </Stack>

          {hasDetails && <Divider sx={dividerStyle} />}

          {jobTitle && (
            <Stack direction="row" spacing={1} alignItems="center">
              <WorkOutlineOutlinedIcon aria-hidden sx={detailIconStyle} />
              <Typography variant="body1" noWrap color={colors.grayMedium}>
                {jobTitle}
              </Typography>
            </Stack>
          )}

          {locationName && (
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOnOutlinedIcon aria-hidden sx={detailIconStyle} />
              <Typography variant="body1" noWrap color={colors.grayMedium}>
                {locationName}
              </Typography>
            </Stack>
          )}

          <CelebrationBanners events={events} firstName={firstName} />
        </Stack>
      </CardActionArea>
      {showRecognize && (
        <Box sx={{ p: 2 }}>
          <RecognizeButton
            id="btnMemberPreviewRecognize"
            variant="contained"
            fullWidth
            stopPropagation
            startIcon={
              <StarOutlineRoundedIcon sx={{ color: `${colors.goldPrimary} !important` }} />
            }
            onRecognized={onClose}
            recipient={toRecognitionRecipient(member)}
            sx={{ mt: 0 }}
          />
        </Box>
      )}
    </Card>
  )
}

export default MemberProfilePreviewCard
